import { useState, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { useAuth } from "@/lib/auth"
import { trackEvent } from "@/lib/posthog"
import { AuthMethodDivider, GoogleAuthButton } from "@/components/auth/GoogleAuthButton"

const DEMO_EMAIL = "demo@stayframe.ai"
const DEMO_PASSWORD = "demo"
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: "Google sign-in was cancelled.",
  account_inactive: "This account is inactive. Contact support if you need access restored.",
  email_exists: "An account with this email already exists. Sign in with your password to continue.",
  email_unverified: "Google did not return a verified email for this account.",
  oauth_failed: "Google sign-in failed. Please try again.",
  oauth_rate_limited: "Too many sign-in attempts. Please try again in a few minutes.",
  oauth_unavailable: "Google sign-in is not configured yet.",
}

export function LoginPage() {
  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const isDemo = params.get("demo") === "true"
  const [email, setEmail] = useState(isDemo ? DEMO_EMAIL : "")
  const [password, setPassword] = useState(isDemo ? DEMO_PASSWORD : "")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const autoSubmitted = useRef(false)
  const nextParam = params.get("next") || ""
  const nextPath = nextParam.startsWith("/") && !nextParam.startsWith("//") && nextParam !== "/login"
    ? nextParam
    : "/"
  const oauthError = params.get("error") || ""
  const oauthErrorMessage = !error ? (OAUTH_ERROR_MESSAGES[oauthError] || "") : ""

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate(nextPath, { replace: true })
    return null
  }

  async function doLogin(e?: React.FormEvent) {
    if (e) e.preventDefault()
    setError("")
    setLoading(true)

    try {
      await login(email, password)
      trackEvent("login_completed", { method: isDemo ? "demo" : "email" })
      navigate(nextPath, { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Login failed"
      trackEvent("login_failed", { error_code: msg })
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  // Auto-submit when arriving from /demo
  useEffect(() => {
    if (isDemo && !autoSubmitted.current && email && password) {
      autoSubmitted.current = true
      doLogin()
    }
  }, [isDemo]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(e: React.FormEvent) {
    doLogin(e)
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-card__brand">
          <div className="login-card__brand-mark">S</div>
          Stayframe
        </div>

        {(error || oauthErrorMessage) && <div className="login-card__error" role="alert">{error || oauthErrorMessage}</div>}

        <GoogleAuthButton nextPath={nextPath} />
        <AuthMethodDivider />

        <div className="login-card__field">
          <label className="login-card__label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className="login-card__input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="login-card__field">
          <label className="login-card__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className="login-card__input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={{ textAlign: "right", marginBottom: "4px" }}>
          <Link
            to="/forgot-password"
            style={{ fontSize: "13px", color: "var(--sf-color-muted-foreground, #71717a)", textDecoration: "none" }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          className="login-card__submit"
          disabled={loading || !email || !password}
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>

        <p style={{ fontSize: "14px", textAlign: "center", color: "var(--sf-color-muted-foreground, #71717a)", marginTop: "16px" }}>
          Don&apos;t have an account?{" "}
          <Link to={nextPath && nextPath !== "/" ? `/sign-up?next=${encodeURIComponent(nextPath)}` : "/sign-up"} style={{ color: "var(--sf-color-primary, #0f766e)", fontWeight: 500, textDecoration: "none" }}>
            Sign up
          </Link>
        </p>
      </form>

      <p style={{ fontSize: "12px", textAlign: "center", color: "var(--sf-color-muted-foreground, #71717a)", opacity: 0.6, marginTop: "24px" }}>
        <a href="https://www.stayframe.ai/terms" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", textUnderlineOffset: "2px", color: "inherit" }}>Terms</a>
        {" · "}
        <a href="https://www.stayframe.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", textUnderlineOffset: "2px", color: "inherit" }}>Privacy</a>
      </p>
    </div>
  )
}
